import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { SupportedLanguagesCodes } from '@/_shared/constants/languages';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { Roles } from '@/_shared/constants/roles';
import { GenericGet } from '@/_shared/types';
import { GenericApiError } from '@/_shared/types/apiError';
import { API_ORGANIZATIONS_ORGANIZATIONID_USERS_ME_PATH_KEY } from '@/swagger/constants/slash-keys';
import { API_USER_PROFILE, KEY_GET_USER_PROFILE } from '../constants';
import { MOCK_RESPONSE_GET_USER_PROFILE } from '../mocks';
import { getCurrentOrganization } from '../selectors';
import { UserStatusEnum, ValueOfClaims } from '../types';

export type CallParamsGetUserProfile = {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type ResponseGetUserProfile = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_USERS_ME_PATH_KEY
>['response']['body'];

type CallResponseGetUserProfile = ResponseGetUserProfile;

type CallErrorGetUserProfile = GenericApiError;

type CallSelectorsGetUserProfile = {
  id: ResponseGetUserProfile['id'];
  firstName: ResponseGetUserProfile['firstName'];
  lastName: ResponseGetUserProfile['lastName'];
  fullName: string;
  email: ResponseGetUserProfile['email'];
  claims: ValueOfClaims[];
  currentUserLanguage: SupportedLanguagesCodes;
  userRole: Roles | undefined;
  activeContext: ResponseGetUserProfile['activeContext'];
  availableContexts: ResponseGetUserProfile['availableContexts'];
  hasMultipleContexts: ResponseGetUserProfile['hasMultipleContexts'];
  firstLogin: ResponseGetUserProfile['firstLogin'];
  hasSkippedConfig: boolean;
};

type CallStateGetUserProfile = {
  hasSkippedConfig?: boolean;
};

type CallReducersGetUserProfile = {
  skipConfig: (payload: undefined) => (state: CallStateGetUserProfile) => CallStateGetUserProfile;
};

export const getUserProfileCall = createGeneralCall<
  CallParamsGetUserProfile,
  CallResponseGetUserProfile,
  CallErrorGetUserProfile,
  CallSelectorsGetUserProfile,
  CallStateGetUserProfile,
  CallReducersGetUserProfile
>({
  id: KEY_GET_USER_PROFILE,
  url: () => state => {
    const org = getCurrentOrganization(state);
    return API_USER_PROFILE(org as unknown as string);
  },
  method: 'get',
  paramsSelector:
    ({ payload }) =>
    () =>
      payload,
  successMock: IS_MOCK_ENABLED({ avoidMockOnDemoMode: true })
    ? MOCK_RESPONSE_GET_USER_PROFILE
    : undefined,
  isSuccessResponse: ({ response }) =>
    response?.roles?.length !== 0 || response.status === UserStatusEnum.Disabled,
  selectors: {
    id: ({ selectors }) => createSelector(selectors.response, res => res?.id || ''),
    firstName: ({ selectors }) => createSelector(selectors.response, res => res?.firstName || ''),
    lastName: ({ selectors }) => createSelector(selectors.response, res => res?.lastName || ''),
    fullName: ({ selectors }) =>
      createSelector(selectors.response, res => {
        if (res && res.firstName !== undefined && res.lastName !== undefined) {
          return `${res.firstName} ${res.lastName}`;
        }
        return 'User';
      }),
    email: ({ selectors }) => createSelector(selectors.response, res => res?.email || ''),
    currentUserLanguage: ({ selectors }) =>
      createSelector(selectors.response, res => {
        if (Array.isArray(res?.claims) && res.claims.length > 0 && res.language) {
          return res.language as SupportedLanguagesCodes;
        }
        return 'it' as SupportedLanguagesCodes;
      }),
    claims: ({ selectors }) =>
      createSelector(selectors.response, res =>
        res && res.claims && res?.claims?.length > 0 ? (res.claims as ValueOfClaims[]) : [],
      ),
    userRole: ({ selectors }) =>
      createSelector(selectors.response, res =>
        res && res.roles && res?.roles?.length > 0 ? (res?.roles[0] as Roles) : undefined,
      ),
    activeContext: ({ selectors }) => createSelector(selectors.response, res => res?.activeContext),
    availableContexts: ({ selectors }) =>
      createSelector(selectors.response, res => res?.availableContexts),
    hasMultipleContexts: ({ selectors }) =>
      createSelector(selectors.response, res => res?.hasMultipleContexts || false),
    firstLogin: ({ selectors }) =>
      createSelector(selectors.response, res => res?.firstLogin || undefined),
    hasSkippedConfig: ({ selectors }) =>
      createSelector(
        selectors.request,
        req => (req as unknown as CallStateGetUserProfile)?.hasSkippedConfig ?? false,
      ),
  },
  reducers: {
    skipConfig: () => state => ({ ...state, hasSkippedConfig: true }),
  },
});
